import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { NotbankService } from '../../../services/notbank.service';
import { countryForNotbankCurrency } from '../add-cashout/currencies/notbank-cashout.logic';
import {
  NOTBANK_CIVIL_STATUS_OPTIONS,
  NOTBANK_FIELD_LABELS,
  NOTBANK_GENDER_OPTIONS,
  NOTBANK_IDENTITY_TYPE_OPTIONS,
  applyCountryRegisterValidators,
  buildRegisterPayloadFromForm,
  createNotbankRegisterFormGroup,
  extraRegisterFieldsForCountry,
} from './notbank-provision.logic';

@Component({
  selector: 'app-notbank-provision-dialog',
  templateUrl: './notbank-provision-dialog.component.html',
  styleUrl: './notbank-provision-dialog.component.scss',
})
export class NotbankProvisionDialogComponent implements OnInit, OnChanges {
  @Input() visible = false;
  @Input() clientId: number | string | null = null;
  @Input() currency = '';
  @Input() missingFields: string[] = [];
  @Input() partialRegister: Record<string, unknown> | null = null;
  /** Optional bank account collected on cashin/cashout form — sent with provision. */
  @Input() bankAccount: Record<string, unknown> | null = null;
  @Input() errorMessage = '';

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() provisioned = new EventEmitter<any>();
  @Output() cancelled = new EventEmitter<void>();

  form!: FormGroup;
  submitting = false;
  private dismissAfterSuccess = false;

  readonly genderOptions = NOTBANK_GENDER_OPTIONS;
  readonly identityTypeOptions = NOTBANK_IDENTITY_TYPE_OPTIONS;
  readonly civilStatusOptions = NOTBANK_CIVIL_STATUS_OPTIONS;
  readonly fieldLabels = NOTBANK_FIELD_LABELS;

  constructor(
    private fb: FormBuilder,
    private notbankService: NotbankService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.rebuildForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['visible']?.currentValue === true ||
      changes['currency'] ||
      changes['partialRegister'] ||
      changes['missingFields']
    ) {
      if (this.visible) {
        this.rebuildForm();
      }
    }
  }

  get country(): string {
    return countryForNotbankCurrency(this.currency);
  }

  get extraFields(): string[] {
    return extraRegisterFieldsForCountry(this.country);
  }

  isMissing(field: string): boolean {
    return (this.missingFields || []).includes(field);
  }

  rebuildForm(): void {
    this.form = createNotbankRegisterFormGroup(
      this.fb,
      this.currency,
      this.partialRegister,
    );
    applyCountryRegisterValidators(this.form, this.currency);
    this.form.get('pep')?.valueChanges.subscribe(() => {
      applyCountryRegisterValidators(this.form, this.currency);
    });
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.cancelled.emit();
  }

  onVisibleChange(value: boolean): void {
    this.visible = value;
    this.visibleChange.emit(value);
    if (!value && !this.dismissAfterSuccess) {
      this.cancelled.emit();
    }
    this.dismissAfterSuccess = false;
  }

  submit(): void {
    if (!this.clientId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Cliente',
        detail: 'Selecciona un cliente antes de provisionar Notbank.',
      });
      return;
    }
    applyCountryRegisterValidators(this.form, this.currency);
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Datos incompletos',
        detail: 'Completa los campos obligatorios del registro Notbank (hijo).',
      });
      return;
    }

    const body: Record<string, unknown> = {
      client_id: Number(this.clientId),
      currency: String(this.currency || '').toUpperCase(),
      local_register: buildRegisterPayloadFromForm(this.form),
    };
    if (this.bankAccount && Object.keys(this.bankAccount).length) {
      body['bank_account'] = this.bankAccount;
    }

    this.submitting = true;
    this.notbankService.provision(body).subscribe({
      next: (resp) => {
        this.submitting = false;
        this.messageService.add({
          severity: 'success',
          summary: 'Notbank',
          detail: 'Cuenta hijo provisionada. Ya puedes operar esta moneda.',
        });
        this.dismissAfterSuccess = true;
        this.visible = false;
        this.visibleChange.emit(false);
        this.provisioned.emit(resp?.data ?? resp);
      },
      error: (err) => {
        this.submitting = false;
        const bodyErr = err?.error ?? {};
        const missing = Array.isArray(bodyErr.missing_fields)
          ? bodyErr.missing_fields
          : [];
        if (missing.length) {
          this.missingFields = missing;
        }
        if (bodyErr.partial_register && typeof bodyErr.partial_register === 'object') {
          this.partialRegister = {
            ...(this.partialRegister || {}),
            ...bodyErr.partial_register,
          };
          this.rebuildForm();
        }
        this.messageService.add({
          severity: 'error',
          summary: 'No se pudo provisionar',
          detail:
            bodyErr.message ||
            err?.message ||
            'Revisa los campos marcados e intenta de nuevo.',
          life: 8000,
        });
      },
    });
  }
}
